/**
 * SSO sign-in / sign-up button cluster.
 *
 * Fetches `/api/auth/sso/providers` once on mount; each enabled+configured
 * provider gets a button that navigates to `/api/auth/sso/<provider>/start
 * ?intent=<intent>`. The Worker performs the redirect to the IDP — we
 * don't need a fetch + follow redirect because the browser does it for us.
 *
 * Dark theme parity is from the first commit (CLAUSE constraint #26):
 * each button uses the surface tokens with `dark:` variants.
 */

import { useEffect, useState } from "react";

export type SsoProvider = "microsoft" | "google" | "cloudflare" | "apple";

export interface SsoProviderInfo {
  provider: SsoProvider;
  enabled: boolean;
  configured: boolean;
}

interface ProvidersResponse {
  success: true;
  data: { sso_enabled: boolean; providers: SsoProviderInfo[] };
}

interface SsoButtonsProps {
  intent: "signin" | "signup" | "link";
  returnTo?: string;
  className?: string;
  hideHeading?: boolean;
}

const LABELS: Record<SsoProvider, string> = {
  microsoft: "Microsoft",
  google: "Google",
  cloudflare: "Cloudflare",
  apple: "Apple",
};

const ACTIONS: Record<"signin" | "signup" | "link", string> = {
  signin: "Sign in with",
  signup: "Sign up with",
  link: "Link",
};

export function SsoButtons({ intent, returnTo, className, hideHeading }: SsoButtonsProps): JSX.Element | null {
  const [providers, setProviders] = useState<SsoProviderInfo[]>([]);
  const [ssoEnabled, setSsoEnabled] = useState<boolean>(true);
  const [loaded, setLoaded] = useState<boolean>(false);

  useEffect(() => {
    let cancelled = false;
    fetch("/api/auth/sso/providers")
      .then((r) => r.json() as Promise<ProvidersResponse>)
      .then((j) => {
        if (cancelled) return;
        setSsoEnabled(j.data.sso_enabled);
        setProviders(j.data.providers);
        setLoaded(true);
      })
      .catch(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!loaded) return null;
  const usable = providers.filter((p) => p.enabled && p.configured);
  if (!ssoEnabled || usable.length === 0) return null;

  const startUrl = (provider: SsoProvider): string => {
    const params = new URLSearchParams({ intent });
    if (returnTo) params.set("return_to", returnTo);
    return `/api/auth/sso/${provider}/start?${params.toString()}`;
  };

  return (
    <div className={className ?? "space-y-3"}>
      {!hideHeading && (
        <div className="relative my-4">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t border-border" />
          </div>
          <div className="relative flex justify-center text-xs uppercase tracking-wider">
            <span className="bg-background px-3 font-mono text-muted-foreground">
              or continue with
            </span>
          </div>
        </div>
      )}
      <div className="grid gap-2">
        {usable.map((p) => (
          <a
            key={p.provider}
            href={startUrl(p.provider)}
            className="inline-flex h-10 items-center justify-center gap-3 rounded-md border border-border bg-surface px-4 text-sm font-medium text-charcoal shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring dark:bg-surface-raised dark:text-charcoal-dark dark:border-border dark:hover:bg-accent/40"
          >
            <ProviderMark provider={p.provider} />
            <span>{`${ACTIONS[intent]} ${LABELS[p.provider]}`}</span>
          </a>
        ))}
      </div>
    </div>
  );
}

function ProviderMark({ provider }: { provider: SsoProvider }): JSX.Element {
  const cls = "h-5 w-5";
  switch (provider) {
    case "microsoft":
      return (
        <svg viewBox="0 0 23 23" className={cls} aria-hidden="true">
          <rect x="1" y="1" width="10" height="10" fill="#F25022" />
          <rect x="12" y="1" width="10" height="10" fill="#7FBA00" />
          <rect x="1" y="12" width="10" height="10" fill="#00A4EF" />
          <rect x="12" y="12" width="10" height="10" fill="#FFB900" />
        </svg>
      );
    case "google":
      return (
        <svg viewBox="0 0 24 24" className={cls} aria-hidden="true">
          <path
            fill="#4285F4"
            d="M22.5 12.27c0-.79-.07-1.55-.2-2.27H12v4.3h5.9c-.25 1.4-1.02 2.58-2.18 3.37v2.8h3.52c2.06-1.9 3.26-4.7 3.26-8.2z"
          />
          <path
            fill="#34A853"
            d="M12 23c2.94 0 5.4-.98 7.2-2.65l-3.52-2.8c-.97.65-2.22 1.05-3.68 1.05-2.83 0-5.23-1.91-6.09-4.49H2.27v2.84A10.99 10.99 0 0 0 12 23z"
          />
          <path
            fill="#FBBC05"
            d="M5.91 14.11A6.6 6.6 0 0 1 5.55 12c0-.74.13-1.45.36-2.11V7.05H2.27A10.97 10.97 0 0 0 1 12c0 1.78.43 3.46 1.27 4.95l3.64-2.84z"
          />
          <path
            fill="#EA4335"
            d="M12 5.4c1.6 0 3.03.55 4.16 1.62l3.12-3.12C17.4 2.13 14.94 1 12 1 7.62 1 3.85 3.5 2.27 7.05L5.91 9.9C6.77 7.31 9.17 5.4 12 5.4z"
          />
        </svg>
      );
    case "cloudflare":
      return (
        <svg viewBox="0 0 48 48" className={cls} aria-hidden="true">
          <path
            fill="#F38020"
            d="M37.7 28.5c-.6 0-1.1.1-1.6.2-.6-1.7-2.1-2.9-4-2.9-1.1 0-2.1.4-2.9 1.1-1-3.6-4.3-6.3-8.2-6.3-4.7 0-8.5 3.8-8.5 8.5 0 .3 0 .5.1.8-3.8.4-6.7 3.6-6.7 7.5 0 4.2 3.4 7.5 7.6 7.5h24.2c3.5 0 6.3-2.8 6.3-6.3 0-3.4-2.8-6.1-6.3-6.1z"
          />
        </svg>
      );
    case "apple":
      return (
        <svg viewBox="0 0 24 24" className={`${cls} fill-current`} aria-hidden="true">
          <path d="M19 17.36a4.6 4.6 0 0 1-2.27 4 5.5 5.5 0 0 1-1.55.59c-.61 0-1-.39-1.78-.39s-1.21.39-1.83.39a4.36 4.36 0 0 1-1.55-.59A6.7 6.7 0 0 1 7.45 14a4.61 4.61 0 0 1 4.34-4.79 3.85 3.85 0 0 1 1.83.49 4 4 0 0 0 1.78.49 5.27 5.27 0 0 0 1.93-.49 4.84 4.84 0 0 1 2.07-.39 4.32 4.32 0 0 1 3.45 1.86 4.65 4.65 0 0 0-2.85 4.27zM14.84 7.7a4.18 4.18 0 0 0 1.05-3.06 4.5 4.5 0 0 0-3 1.55 4.13 4.13 0 0 0-1 3 3.43 3.43 0 0 0 2.95-1.49z" />
        </svg>
      );
  }
}
